import { useState, useEffect, useRef, useCallback } from 'react'
import { renderMessageWithLinks } from '../../utils/messageUtils'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Avatar,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
  Spinner,
  useToast,
  Image,
  useDisclosure,
  useColorMode,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Textarea,
  Select,
  Progress,
  Alert,
  AlertDescription,
  CloseButton,
} from '@chakra-ui/react'
import {
  PaperPlaneRight,
  DotsThreeVertical,
  UserCircle,
  Flag,
  CheckCircle,
  ArrowsLeftRight,
  Paperclip,
  Image as ImageIcon,
  FileDoc,
  Play,
  Download,
  X,
  Microphone,
  Stop,
  Check,
  Checks,
  Clock,
  Info,
  CaretDown,
  CaretLeft,
  PencilSimple,
  Trash,
  Smiley,
  ArrowBendUpLeft,
  Tag,
  Robot,
  WarningCircle,
} from '@phosphor-icons/react'
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import { useDropzone } from 'react-dropzone'
import Lightbox from 'yet-another-react-lightbox'
import { formatMessageTime } from '../../utils/dateUtils'
import 'yet-another-react-lightbox/styles.css'
import api from '../../services/api'
import { useSocket } from '../../contexts/SocketContext'

/** "digitando..." do cliente some sozinho se o typing:stopped não chegar. */
const TYPING_EXPIRY_MS = 12_000
import { useAuth } from '../../contexts/AuthContext'
import { janelaAtiva, aoFicarAtiva } from '../../utils/janelaAtiva'
import { useFileUpload } from '../../hooks/useFileUpload'
import { UploadProgressList } from '../../components/UploadProgress'
import { VideoThumbnail } from '../../components/VideoPlayer/VideoThumbnail'
import { AudioPlayer } from '../../components/AudioPlayer'
import ConfirmModal from '../../components/ConfirmModal'
import AccessTags from '../../components/AccessTags'
import { GuestBadge } from './GuestInfoPanel'
import { WhatsappBadge, formatWaId, isPlaceholderGuestEmail } from '../../components/WhatsappBadge'
import { onResync } from '../../utils/resync'
import { newClientMessageId } from '../../utils/clientMessageId'
// Som de notificação
import notificationSound from '../../assets/notificacao-wedrop.mp3'

/** Status de entrega de uma mensagem do agente no canal WhatsApp (support_whatsapp_outbox). */
interface WhatsappDelivery {
  status: string
  lastError?: string | null
  sentAt?: string | null
  deliveredAt?: string | null
  readAt?: string | null
}

interface Message {
  id: number | string
  message: string
  messageType: 'text' | 'image' | 'video' | 'audio' | 'file'
  fileUrl: string | null
  fileType: string | null
  fileName: string | null
  fileSize: number | null
  caption: string | null
  senderType: 'user' | 'agent' | 'system'
  senderName: string
  senderProfilePic: string | null
  createdAt: string
  editedAt: string | null
  deletedAt: string | null
  seenByAgentAt: string | null
  seenByUserAt: string | null
  sending?: boolean
  error?: boolean
  /** Id do envio, reaproveitado no "Reenviar" (idempotência na API) */
  clientMessageId?: string
  replyToMessageId?: number | null
  replyToUserName?: string | null
  replyToMessage?: string | null
}

interface CustomStatus {
  id: number
  name: string
  color: string
  requiresReason: boolean
}

function MessageBubble({
  message,
  isOwn,
  onImageClick,
  onRetry,
  onEdit,
  onDelete,
  onReply,
  waStatus,
}: {
  message: Message
  isOwn: boolean
  /** Só em chats do WhatsApp: status de entrega da mensagem. */
  waStatus?: WhatsappDelivery
  onImageClick: (url: string) => void
  onRetry?: (message: Message) => void
  onEdit?: (messageId: number | string, newText: string) => void
  onDelete?: (messageId: number | string) => void
  onReply?: (message: Message) => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(message.message)
  const [isDownloading, setIsDownloading] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const otherBubbleBg = useColorModeValue('gray.200', 'gray.700')
  const deletedBubbleBg = useColorModeValue('gray.100', 'gray.800')
  const otherTextColor = useColorModeValue('gray.800', 'gray.100')
  const otherSenderColor = useColorModeValue('yellow.600', 'yellow.400')
  const otherReplyBg = useColorModeValue('gray.300', 'gray.600')
  const otherReplyNameColor = useColorModeValue('yellow.700', 'yellow.200')
  const otherReplyTextColor = useColorModeValue('gray.600', 'gray.300')
  const fileBgOther = useColorModeValue('gray.300', 'gray.600')
  const fileSizeColor = useColorModeValue('gray.500', 'gray.400')
  const timeColorOther = useColorModeValue('gray.500', 'gray.500')
  const deletedBorderColor = useColorModeValue('gray.300', 'gray.600')
  const menuItemBg = useColorModeValue('white', 'gray.800')
  const menuItemHoverBg = useColorModeValue('gray.100', 'gray.700')
  const menuListBorderColor = useColorModeValue('gray.200', 'gray.600')
  const caretColor = useColorModeValue('gray.500', 'gray.400')

  const isDeleted = !!message.deletedAt
  const canEdit = isOwn && !message.sending && !message.error && !isDeleted && message.senderType !== 'system'
  const canShowMenu = !message.sending && !message.error && !isDeleted && message.senderType !== 'system'

  const handleEdit = () => {
    setEditText(message.message)
    setIsEditing(true)
  }

  const handleSaveEdit = () => {
    if (editText.trim() && editText.trim() !== message.message && onEdit) {
      onEdit(message.id, editText.trim())
    }
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setEditText(message.message)
    setIsEditing(false)
  }

  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  const confirmDelete = () => {
    if (onDelete) {
      onDelete(message.id)
    }
  }

  const renderContent = () => {
    if (isDeleted) {
      return (
        <Text fontSize="sm" color="gray.500" whiteSpace="pre-wrap">
          {message.message}
        </Text>
      )
    }

    if (isEditing) {
      return (
        <VStack align="stretch" spacing={2}>
          <Textarea
            size="sm"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit() }
              if (e.key === 'Escape') handleCancelEdit()
            }}
            bg="white"
            color="gray.900"
            autoFocus
            rows={2}
            minH="40px"
            maxH="150px"
            resize="none"
          />
          <HStack spacing={1} justify="flex-end">
            <IconButton aria-label="Cancelar" icon={<X size={14} />} size="xs" variant="ghost" onClick={handleCancelEdit} />
            <IconButton aria-label="Salvar" icon={<Check size={14} />} size="xs" colorScheme="green" onClick={handleSaveEdit} />
          </HStack>
        </VStack>
      )
    }

    switch (message.messageType) {
      case 'image':
        return (
          <VStack align={isOwn ? 'end' : 'start'} spacing={1}>
            <Image
              src={message.fileUrl || ''}
              alt={message.caption || 'Imagem'}
              maxH="300px"
              maxW="300px"
              borderRadius="lg"
              cursor="pointer"
              onClick={() => message.fileUrl && onImageClick(message.fileUrl)}
            />
            {message.caption && (
              <Text fontSize="sm" color={isOwn ? 'gray.900' : otherTextColor} whiteSpace="pre-wrap">
                {message.caption}
              </Text>
            )}
          </VStack>
        )

      case 'video':
        return (
          <VStack align={isOwn ? 'end' : 'start'} spacing={1}>
            <VideoThumbnail src={message.fileUrl || ''} maxWidth="280px" maxHeight="200px" isOwn={isOwn} />
            {message.caption && (
              <Text fontSize="sm" color={isOwn ? 'gray.900' : otherTextColor} whiteSpace="pre-wrap">
                {message.caption}
              </Text>
            )}
          </VStack>
        )

      case 'audio':
        if (!message.fileUrl) {
          return (
            <HStack spacing={2} py={1}>
              <Spinner size="sm" color={isOwn ? 'gray.700' : fileSizeColor} />
              <Text fontSize="sm" color={isOwn ? 'gray.700' : fileSizeColor}>
                Enviando áudio...
              </Text>
            </HStack>
          )
        }
        return (
          <AudioPlayer src={message.fileUrl} isOwn={isOwn} />
        )

      case 'file':
        return (
          <VStack align={isOwn ? 'end' : 'start'} spacing={1}>
            <HStack
              p={3}
              bg={isOwn ? 'gray.900' : fileBgOther}
              borderRadius="lg"
              spacing={3}
            >
              <FileDoc size={32} />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                  {message.fileName || 'Arquivo'}
                </Text>
                <Text fontSize="xs" color={fileSizeColor}>
                  {message.fileSize
                    ? `${(message.fileSize / 1024).toFixed(1)} KB`
                    : 'Arquivo'}
                </Text>
              </VStack>
              <IconButton
                aria-label="Download"
                icon={isDownloading ? <Spinner size="sm" /> : <Download size={20} />}
                size="sm"
                variant="ghost"
                isDisabled={isDownloading}
                onClick={async (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (isDownloading) return
                  setIsDownloading(true)
                  try {
                    const res = await api.get(`/messages/${message.id}/download`, { responseType: 'blob' })
                    const blob = new Blob([res.data])
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = message.fileName || 'file'
                    a.style.display = 'none'
                    document.body.appendChild(a)
                    a.click()
                    setTimeout(() => {
                      document.body.removeChild(a)
                      URL.revokeObjectURL(url)
                    }, 100)
                  } catch {
                    window.open(message.fileUrl || '', '_blank')
                  } finally {
                    setIsDownloading(false)
                  }
                }}
              />
            </HStack>
            {message.caption && (
              <Text fontSize="sm" color={isOwn ? 'gray.900' : otherTextColor} whiteSpace="pre-wrap">
                {message.caption}
              </Text>
            )}
          </VStack>
        )

      default:
        return (
          <Text
            fontSize="sm"
            color={isOwn ? 'gray.900' : otherTextColor}
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {renderMessageWithLinks(message.message)}
          </Text>
        )
    }
  }

  const renderMessageStatus = () => {
    if (!isOwn) return null

    if (message.sending) {
      return <Clock size={12} color="#A0AEC0" />
    }
    if (message.error) {
      return (
        <HStack spacing={1} cursor="pointer" title="Clique para reenviar">
          <X size={12} color="#E53E3E" />
        </HStack>
      )
    }
    if (waStatus) {
      const err = waStatus.lastError ? ` (${waStatus.lastError})` : ''
      switch (waStatus.status) {
        case 'queued':
          return <span title="Na fila para envio ao WhatsApp"><Clock size={12} color="#A0AEC0" /></span>
        case 'sent':
          return <span title="Enviada ao WhatsApp"><Check size={12} color="#A0AEC0" /></span>
        case 'delivered':
          return <span title="Entregue"><Checks size={12} weight="bold" color="#A0AEC0" /></span>
        case 'read':
          return <span title="Lida"><Checks size={12} weight="bold" color="#63B3ED" /></span>
        case 'skipped_window':
          return <span title="Não enviada: fora da janela de 24h do WhatsApp (o cliente precisa escrever primeiro)"><WarningCircle size={12} color="#DD6B20" /></span>
        case 'skipped_optout':
          return <span title="Não enviada: o cliente pediu para não receber mensagens"><WarningCircle size={12} color="#DD6B20" /></span>
        case 'failed':
          return <span title={`Falha no envio${err}`}><X size={12} color="#E53E3E" /></span>
      }
    }
    if (message.seenByUserAt) {
      return <Checks size={12} weight="bold" color="#63B3ED" />
    }
    return <Check size={12} color="#A0AEC0" />
  }

  if (message.senderType === 'system') {
    return (
      <Flex justify="center" my={2} px={2}>
        <Badge 
          colorScheme="gray" 
          px={3} 
          py={1} 
          borderRadius="full"
          maxW="100%"
          wordBreak="break-word"
          whiteSpace="normal"
          textAlign="center"
        >
          {message.message}
        </Badge>
      </Flex>
    )
  }

  return (
    <Flex justify={isOwn ? 'flex-end' : 'flex-start'} mb={2} role="group" px={1}>
      <HStack align="end" spacing={2} maxW={{ base: '85%', md: '75%' }} minW={0}>
        {!isOwn && (
          <Avatar
            size="xs"
            name={message.senderName}
            src={message.senderProfilePic || undefined}
            bg="gray.600"
          />
        )}
        <Box position="relative">
          {canShowMenu && (
            <Menu placement="bottom-end" isLazy>
              <MenuButton
                as={IconButton}
                icon={<CaretDown size={14} />}
                size="xs"
                variant="ghost"
                position="absolute"
                top={1}
                right={1}
                minW="auto"
                h="auto"
                p={0.5}
                opacity={0}
                _groupHover={{ opacity: 1 }}
                zIndex={1}
                color={isOwn ? 'gray.700' : caretColor}
                aria-label="Opções"
              />
              <MenuList minW="140px" bg={menuItemBg} borderColor={menuListBorderColor}>
                <MenuItem icon={<ArrowBendUpLeft size={16} />} onClick={() => onReply?.(message)} bg={menuItemBg} _hover={{ bg: menuItemHoverBg }}>
                  Responder
                </MenuItem>
                {canEdit && message.messageType === 'text' && (
                  <MenuItem icon={<PencilSimple size={16} />} onClick={handleEdit} bg={menuItemBg} _hover={{ bg: menuItemHoverBg }}>
                    Editar
                  </MenuItem>
                )}
                {canEdit && (
                  <MenuItem icon={<Trash size={16} />} onClick={handleDelete} bg={menuItemBg} _hover={{ bg: menuItemHoverBg }} color="red.400">
                    Excluir
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          )}
          <Box
            bg={isDeleted ? deletedBubbleBg : isOwn ? 'yellow.500' : otherBubbleBg}
            px={{ base: 3, md: 4 }}
            py={{ base: 1.5, md: 2 }}
            borderRadius="lg"
            borderBottomRightRadius={isOwn ? 0 : 'lg'}
            borderBottomLeftRadius={isOwn ? 'lg' : 0}
            opacity={message.sending ? 0.7 : 1}
            cursor={message.error ? 'pointer' : 'default'}
            onClick={() => message.error && onRetry && onRetry(message)}
            title={message.error ? 'Clique para reenviar' : undefined}
            border={message.error ? '2px solid' : isDeleted ? '1px dashed' : 'none'}
            borderColor={message.error ? 'red.500' : isDeleted ? deletedBorderColor : 'transparent'}
            maxW="100%"
            minW={0}
          >
            <Text fontSize="xs" fontWeight="semibold" color={isOwn ? 'gray.700' : otherSenderColor} mb={1}>
              {message.senderName}
            </Text>
            {message.replyToMessageId && message.replyToMessage && (
              <Box
                bg={isOwn ? 'yellow.600' : otherReplyBg}
                borderLeft="3px solid"
                borderColor={isOwn ? 'gray.700' : 'yellow.400'}
                borderRadius="md"
                px={2}
                py={1}
                mb={1}
              >
                <Text fontSize="xs" fontWeight="bold" color={isOwn ? 'gray.800' : otherReplyNameColor} noOfLines={1}>
                  {message.replyToUserName || 'Usuário'}
                </Text>
                <Text fontSize="xs" color={isOwn ? 'gray.700' : otherReplyTextColor} noOfLines={2}>
                  {message.replyToMessage}
                </Text>
              </Box>
            )}
            {renderContent()}
            <HStack justify="flex-end" mt={1} spacing={1} flexWrap="wrap">
              <Text fontSize={{ base: '2xs', md: 'xs' }} color={isDeleted ? deletedBorderColor : isOwn ? 'gray.700' : timeColorOther}>
                {formatMessageTime(message.createdAt)}
              </Text>
              {message.editedAt && !isDeleted && (
                <Text fontSize={{ base: '2xs', md: 'xs' }} color={isOwn ? 'gray.600' : timeColorOther}>
                  (editada)
                </Text>
              )}
              {renderMessageStatus()}
            </HStack>
            {message.error && (
              <Text fontSize="xs" color="red.300" textAlign="center" mt={1}>
                Falha no envio - toque para reenviar
              </Text>
            )}
          </Box>
        </Box>
        {isOwn && (
          <Avatar
            size="xs"
            name={message.senderName}
            src={message.senderProfilePic || undefined}
            bg="yellow.600"
          />
        )}
      </HStack>
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Excluir Mensagem"
        message="Tem certeza que deseja excluir esta mensagem?"
        confirmLabel="Excluir"
      />
    </Flex>
  )
}

export default function Chat({
  chatId,
  onToggleUserInfo,
  onChatFinished,
  onClose,
}: {
  chatId: number
  onToggleUserInfo: () => void
  onChatFinished?: () => void
  onClose?: () => void
}) {
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  // Trava síncrona: Enter duas vezes antes do setIsSending assentar mandava 2x
  const sendingRef = useRef(false)
  const [chat, setChat] = useState<any>(null)
  const [waStatuses, setWaStatuses] = useState<Record<string, WhatsappDelivery>>({})
  const [customStatuses, setCustomStatuses] = useState<CustomStatus[]>([])
  const [lightboxImage, setLightboxImage] = useState<string | null>(null)
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([])
  const [isTyping, setIsTyping] = useState(false)
  const [typingUser, setTypingUser] = useState<string | null>(null)

  // Reply state
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)

  // Quick replies state
  const [showQuickReplyMenu, setShowQuickReplyMenu] = useState(false)
  const [quickReplyFilter, setQuickReplyFilter] = useState('')
  const [quickReplies, setQuickReplies] = useState<{ id: number; command: string; description: string; message: string; type: string }[]>([])
  const [quickReplyIndex, setQuickReplyIndex] = useState(0)
  const quickRepliesLoadedRef = useRef(false)

  // Audio recording state
  const [isRecording, setIsRecording] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout>()
  // Expiração do "digitando" do CLIENTE (o de cima é o do próprio atendente)
  const typingExpiryRef = useRef<NodeJS.Timeout>()
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const toast = useToast()
  const { user, canManageSystem } = useAuth()
  const { uploads, uploadFile, cancelUpload, clearUpload } = useFileUpload()

  // Agentes de IA (admin): habilita "Transferir para IA" quando a categoria tem agente ativo
  const [aiAgents, setAiAgents] = useState<any[]>([])
  useEffect(() => {
    if (!canManageSystem) return
    api
      .get('/admin/ai-agents')
      .then((res) => setAiAgents(res.data.data || []))
      .catch(() => {})
  }, [canManageSystem])
  const { colorMode } = useColorMode()
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const headerTextColor = useColorModeValue('gray.800', 'white')
  const mutedColor = useColorModeValue('gray.500', 'gray.400')
  const hoverBg = useColorModeValue('gray.100', 'gray.700')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const recordingTextColor = useColorModeValue('gray.800', 'white')
  const quickReplyBg = useColorModeValue('white', 'gray.700')
  const quickReplyBorder = useColorModeValue('gray.200', 'gray.600')
  const quickReplyActiveBg = useColorModeValue('gray.100', 'gray.600')
  const quickReplyTextColor = useColorModeValue('gray.600', 'gray.300')
  const replyBorderColor = useColorModeValue('gray.200', 'gray.700')
  const replyNameColor = useColorModeValue('yellow.600', 'yellow.300')
  const replyMsgColor = useColorModeValue('gray.500', 'gray.400')
  const transferBannerBg = useColorModeValue('blue.50', 'blue.900')
  const transferBannerText = useColorModeValue('blue.700', 'blue.100')
  const transferBannerLabel = useColorModeValue('blue.800', 'blue.200')
  const transferBannerClose = useColorModeValue('blue.600', 'blue.200')
  const categoryModalSelectBg = useColorModeValue('gray.100', 'gray.700')
  const categoryModalSelectBorder = useColorModeValue('gray.300', 'gray.600')
  const categoryModalTextColor = useColorModeValue('gray.600', 'gray.400')
  const categoryModalSelectColor = useColorModeValue('gray.800', 'white')
  const categoryOptionBg = useColorModeValue('#EDF2F7', '#2D3748')
  
  // Inicializar áudio de notificação
  useEffect(() => {
    notificationAudioRef.current = new Audio(notificationSound)
    notificationAudioRef.current.preload = 'auto'
  }, [])

  const {
    joinChat,
    leaveChat,
    onNewMessage,
    onMessageEdited,
    onMessageDeleted,
    onMessageStatus,
    onTypingStarted,
    onTypingStopped,
    onMessagesRead,
    onChatUpdated,
    startTyping,
    stopTyping,
    markMessagesRead,
  } = useSocket()

  // Canal WhatsApp: status de entrega das mensagens do agente (outbox). Busca ao
  // abrir e a cada message:status; falha silenciosa (é só um indicador).
  const isWhatsappChat = chat?.channel === 'whatsapp'
  const fetchWaStatuses = useCallback(async () => {
    try {
      const res = await api.get(`/chats/${chatId}/whatsapp-status`)
      setWaStatuses(res.data || {})
    } catch {
      /* indicador opcional */
    }
  }, [chatId])

  useEffect(() => {
    setWaStatuses({})
    if (isWhatsappChat) fetchWaStatuses()
  }, [isWhatsappChat, fetchWaStatuses])

  useEffect(() => {
    if (!isWhatsappChat) return
    return onMessageStatus((data: any) => {
      if (Number(data?.chatId) !== Number(chatId)) return
      fetchWaStatuses()
    })
  }, [isWhatsappChat, onMessageStatus, chatId, fetchWaStatuses])

  // Visitante vinculado a uma conta (por este ou outro atendente): recarrega o
  // cabeçalho. chat:updated também chega a cada mensagem — filtra pela ação.
  useEffect(() => {
    const unsub = onChatUpdated(async (data: any) => {
      if (data?.action !== 'guest_linked' || Number(data.chatId) !== Number(chatId)) return
      try {
        const res = await api.get(`/chats/${chatId}`)
        setChat(res.data)
      } catch (error) {
        console.error('Error refreshing chat after guest link:', error)
      }
    })
    return unsub
  }, [onChatUpdated, chatId])

  // "Visto" só com a aba visível e em foco (mesmo critério do service
  // worker): senão o cliente veria "visto" numa aba escondida e o push do
  // atendente seria cancelado sem ninguém ler. Represa e envia ao voltar.
  const leituraPendenteRef = useRef(false)
  const marcarLidoRef = useRef<() => void>(() => {})
  marcarLidoRef.current = () => {
    if (!janelaAtiva()) {
      leituraPendenteRef.current = true
      return
    }
    leituraPendenteRef.current = false
    markMessagesRead(chatId)
  }
  useEffect(() => {
    return aoFicarAtiva(() => {
      if (leituraPendenteRef.current) marcarLidoRef.current()
    })
  }, [chatId])

  // Status change modal
  const {
    isOpen: isStatusModalOpen,
    onOpen: onStatusModalOpen,
    onClose: onStatusModalClose,
  } = useDisclosure()
  const [selectedStatus, setSelectedStatus] = useState<number | null>(null)
  const [statusReason, setStatusReason] = useState('')

  // Transfer modal
  const {
    isOpen: isTransferModalOpen,
    onOpen: onTransferModalOpen,
    onClose: onTransferModalClose,
  } = useDisclosure()
  const [transferAgentId, setTransferAgentId] = useState<number | null>(null)
  const [transferReason, setTransferReason] = useState('')
  const [availableAgents, setAvailableAgents] = useState<any[]>([])
  
  // Category change modal
  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose,
  } = useDisclosure()
  const [categories, setCategories] = useState<{ id: number; name: string; color: string }[]>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')

  // Transfer reason banner
  const [showTransferBanner, setShowTransferBanner] = useState(true)

  // Dropzone
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setUploadingFiles(acceptedFiles)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.webm'],
      'audio/*': ['.mp3', '.wav', '.ogg', '.webm'],
      'application/*': ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.zip', '.rar'],
    },
    multiple: true,
    noClick: true,
  })

  // Fetch chat and messages
  useEffect(() => {
    // Reset transfer banner when chat changes
    setShowTransferBanner(true)
    
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const [chatRes, messagesRes, statusesRes] = await Promise.all([
          api.get(`/chats/${chatId}`),
          api.get(`/messages/${chatId}`),
          api.get('/status'),
        ])
        setChat(chatRes.data)
        
        // Ordenar mensagens por ID ASC (ordem de criação)
        const rawMessages = messagesRes.data.data || []
        rawMessages.sort((a: Message, b: Message) => Number(a.id) - Number(b.id))
        setMessages(rawMessages)
        setCustomStatuses(statusesRes.data)
        
        // Marcar mensagens como lidas via socket (notificar o usuário)
        marcarLidoRef.current()
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
    joinChat(chatId)

    return () => {
      leaveChat(chatId)
    }
  }, [chatId, joinChat, leaveChat])

  // App voltou do segundo plano / socket reconectou / puxou para atualizar: as
  // mensagens que chegaram com o socket fora não vêm por evento. Busca de novo
  // sem spinner (a conversa continua na tela).
  useEffect(
    () =>
      onResync(() => {
        Promise.all([api.get(`/chats/${chatId}`), api.get(`/messages/${chatId}`)])
          .then(([chatRes, messagesRes]) => {
            setChat(chatRes.data)
            const rawMessages = messagesRes.data.data || []
            rawMessages.sort((a: Message, b: Message) => Number(a.id) - Number(b.id))
            setMessages(rawMessages)
            marcarLidoRef.current()
          })
          .catch((error) => console.error('Error resyncing chat:', error))
      }),
    [chatId]
  )

  // Subscribe to socket events
  useEffect(() => {
    // Trocou de chat: zera o "digitando" do chat anterior. Sem isso o nome do
    // cliente antigo ficava preso no cabeçalho/rodapé do chat novo, porque o
    // typing:stopped dele chega com outro chatId e é (corretamente) ignorado.
    setIsTyping(false)
    setTypingUser(null)
    if (typingExpiryRef.current) {
      clearTimeout(typingExpiryRef.current)
      typingExpiryRef.current = undefined
    }

    const unsubMessage = onNewMessage((data) => {
      if (data.chatId === chatId) {
        console.log('[SOCKET] Received message:new', data)
        
        // Som só com a aba ativa: fora dela quem avisa é o push do navegador
        if (data.senderType === 'user' && janelaAtiva()) {
          notificationAudioRef.current?.play().catch(() => {})
        }
        
        setMessages((prev) => {
          // Evitar duplicatas pelo ID
          const existsById = prev.some((msg) => msg.id === data.id)
          if (existsById) {
            return prev.map((msg) => msg.id === data.id ? { ...msg, ...data, sending: false } : msg)
          }
          
          // Verificar se é uma mensagem temporária que chegou do servidor
          const tempIndex = prev.findIndex(
            (msg) => msg.sending && 
            msg.senderType === data.senderType && 
            (msg.message === data.message || (msg.messageType !== 'text' && msg.messageType === data.messageType))
          )
          
          if (tempIndex !== -1) {
            // Substituir mensagem temporária pela real
            const updated = [...prev]
            updated[tempIndex] = { ...data, sending: false }
            return updated
          }
          
          // Adicionar nova mensagem e ordenar por ID ASC
          const newMessages = [...prev, { ...data, sending: false }]
          newMessages.sort((a, b) => Number(a.id) - Number(b.id))
          return newMessages
        })
        
        // Mark as read if we receive it from user (and actually looking)
        if (data.senderType === 'user') {
          marcarLidoRef.current()
        }
      }
    })

    const unsubTypingStart = onTypingStarted((data) => {
      if (Number(data.chatId) === Number(chatId) && data.userId !== user?.id) {
        setIsTyping(true)
        setTypingUser(data.userName)
        // Validade curta: se o typing:stopped se perder (aba fechada, socket
        // caiu, troca de pod), o indicador some sozinho
        if (typingExpiryRef.current) clearTimeout(typingExpiryRef.current)
        typingExpiryRef.current = setTimeout(() => {
          setIsTyping(false)
          setTypingUser(null)
        }, TYPING_EXPIRY_MS)
      }
    })

    const unsubTypingStop = onTypingStopped((data) => {
      if (Number(data.chatId) === Number(chatId)) {
        setIsTyping(false)
        setTypingUser(null)
        if (typingExpiryRef.current) {
          clearTimeout(typingExpiryRef.current)
          typingExpiryRef.current = undefined
        }
      }
    })

    const unsubRead = onMessagesRead((data) => {
      if (data.chatId === chatId) {
        setMessages((prev) =>
          prev.map((msg) => {
            // Quando o usuário leu (readBy === 'user'), atualizar seenByUserAt nas mensagens do agente
            if (data.readBy === 'user' && msg.senderType === 'agent' && !msg.seenByUserAt) {
              return { ...msg, seenByUserAt: data.readAt || new Date().toISOString() }
            }
            // Quando o agente leu (readBy === 'agent'), atualizar seenByAgentAt nas mensagens do usuário
            if (data.readBy === 'agent' && msg.senderType === 'user' && !msg.seenByAgentAt) {
              return { ...msg, seenByAgentAt: data.readAt || new Date().toISOString() }
            }
            return msg
          })
        )
      }
    })

    const unsubEdited = onMessageEdited((data) => {
      if (data.chatId === chatId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === data.id ? { ...msg, message: data.message, editedAt: data.editedAt } : msg
          )
        )
      }
    })

    const unsubDeleted = onMessageDeleted((data) => {
      if (data.chatId === chatId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === data.id ? { ...msg, message: 'Mensagem excluída', deletedAt: data.deletedAt } : msg
          )
        )
      }
    })

    return () => {
      unsubMessage()
      unsubTypingStart()
      unsubTypingStop()
      unsubRead()
      unsubEdited()
      unsubDeleted()
    }
  }, [chatId, onNewMessage, onMessageEdited, onMessageDeleted, onTypingStarted, onTypingStopped, onMessagesRead, markMessagesRead, user?.id])

  // Função para fazer scroll até o final
  const scrollToBottom = useCallback((smooth = true) => {
    // Método 1: scrollIntoView no elemento final
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      })
    }
    
    // Método 2: scrollTop no container (fallback)
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current
      container.scrollTop = container.scrollHeight
    }
  }, [])

  // Scroll to bottom when messages change
  useEffect(() => {
    // Scroll imediato sem animação para carregamento inicial
    scrollToBottom(false)
    
    // Scroll com delay para garantir que o DOM foi atualizado
    const timer1 = setTimeout(() => scrollToBottom(true), 50)
    const timer2 = setTimeout(() => scrollToBottom(true), 150)
    const timer3 = setTimeout(() => scrollToBottom(true), 300)
    
    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
      clearTimeout(timer3)
    }
  }, [messages, scrollToBottom])

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
        ? 'audio/ogg;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : ''
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' })
        setAudioBlob(audioBlob)
        stream.getTracks().forEach(track => track.stop())
      }
      
      mediaRecorder.start(100)
      setIsRecording(true)
      setRecordingDuration(0)
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1)
      }, 1000)
    } catch (error) {
      console.error('Error starting recording:', error)
      toast({
        title: 'Erro ao iniciar gravação',
        description: 'Verifique se o microfone está disponível',
        status: 'error',
        duration: 5000,
      })
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current)
        recordingIntervalRef.current = null
      }
    }
  }

  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop())
    }
    setIsRecording(false)
    setAudioBlob(null)
    setRecordingDuration(0)
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current)
      recordingIntervalRef.current = null
    }
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Handle typing indicator
  const handleTyping = () => {
    startTyping(chatId)

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    typingTimeoutRef.current = setTimeout(() => {
      stopTyping(chatId)
    }, 2000)
  }

  // Editar mensagem
  const handleEditMessage = async (messageId: number | string, newText: string) => {
    try {
      await api.put(`/messages/${messageId}/edit`, { message: newText })
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, message: newText, editedAt: new Date().toISOString() } : msg
        )
      )
    } catch {
      toast({ title: 'Erro ao editar mensagem', status: 'error', duration: 3000 })
    }
  }

  // Excluir mensagem
  const handleDeleteMessage = async (messageId: number | string) => {
    try {
      await api.delete(`/messages/${messageId}`)
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, message: 'Mensagem excluída', deletedAt: new Date().toISOString() } : msg
        )
      )
    } catch {
      toast({ title: 'Erro ao excluir mensagem', status: 'error', duration: 3000 })
    }
  }

  // Confirma a mensagem temporária com a resposta do POST (o socket pode ter
  // chegado antes, ou nem chegar quando a API reconhece um envio repetido)
  const confirmSent = (tempId: string, data: any) => {
    if (!data?.id) return
    setMessages((prev) => {
      if (prev.some((m) => m.id === data.id)) return prev.filter((m) => m.id !== tempId)
      return prev.map((m) =>
        m.id === tempId
          ? {
              ...m,
              id: data.id,
              createdAt: data.createdAt ?? m.createdAt,
              fileUrl: data.fileUrl ?? m.fileUrl,
              sending: false,
              error: false,
            }
          : m
      )
    })
  }

  // Reenviar mensagem com erro — com o MESMO clientMessageId: se o primeiro
  // POST tinha chegado ao servidor, a API devolve a mensagem existente
  const handleRetry = async (message: Message) => {
    if (message.messageType !== 'text' && message.fileUrl?.startsWith('blob:')) {
      // Upload nunca terminou: não há arquivo no servidor para reenviar
      toast({ title: 'Anexe o arquivo de novo para reenviar', status: 'warning', duration: 4000 })
      return
    }
    const clientMessageId = message.clientMessageId || newClientMessageId()
    const tempId = `temp-${Date.now()}`
    setMessages((prev) =>
      prev.map((m) =>
        m.id === message.id ? { ...m, id: tempId, clientMessageId, sending: true, error: false } : m
      )
    )
    try {
      const res = await api.post(`/messages/${chatId}`, {
        message: message.message || '',
        messageType: message.messageType,
        fileUrl: message.fileUrl,
        fileType: message.fileType,
        fileName: message.fileName,
        fileSize: message.fileSize,
        caption: message.caption,
        clientMessageId,
        ...(message.replyToMessageId ? { replyToMessageId: message.replyToMessageId } : {}),
      })
      confirmSent(tempId, res.data)
    } catch {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === tempId ? { ...msg, sending: false, error: true } : msg))
      )
      toast({
        title: 'Erro ao reenviar',
        status: 'error',
        duration: 3000,
      })
    }
  }

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items
    if (!items) return

    const files: File[] = []
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.kind === 'file') {
        const file = item.getAsFile()
        if (file) files.push(file)
      }
    }

    if (files.length > 0) {
      e.preventDefault()
      setUploadingFiles(prev => [...prev, ...files])
    }
  }, [])

  const autoResizeTextarea = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`
  }, [])

  useEffect(() => {
    autoResizeTextarea()
  }, [newMessage, autoResizeTextarea])

  const handleEmojiSelect = useCallback((emoji: any) => {
    const sym = emoji.native as string
    setNewMessage(prev => prev + sym)
    setShowEmojiPicker(false)
    textareaRef.current?.focus()
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false)
      }
    }
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showEmojiPicker])

  // Pre-fetch quick replies
  useEffect(() => {
    if (quickRepliesLoadedRef.current) return
    quickRepliesLoadedRef.current = true
    api.get('/quick-replies').then((res) => {
      setQuickReplies(res.data)
    }).catch(() => {})
  }, [])

  const filteredQuickReplies = quickReplies.filter((r) =>
    r.command.toLowerCase().includes(quickReplyFilter.toLowerCase()) ||
    r.description.toLowerCase().includes(quickReplyFilter.toLowerCase())
  )

  const handleQuickReplySelect = (reply: { message: string }) => {
    setNewMessage(reply.message)
    setShowQuickReplyMenu(false)
    setQuickReplyFilter('')
    setQuickReplyIndex(0)
    textareaRef.current?.focus()
  }

  // Send message
  const handleSend = async () => {
    if (!newMessage.trim() && uploadingFiles.length === 0 && !audioBlob) return
    if (sendingRef.current) return
    sendingRef.current = true

    setIsSending(true)
    stopTyping(chatId)

    try {
      // Enviar áudio gravado
      if (audioBlob) {
        const tempId = `temp-${Date.now()}`
        const clientMessageId = newClientMessageId()
        const localBlobUrl = URL.createObjectURL(audioBlob)
        const tempMessage: Message = {
          id: tempId,
          clientMessageId,
          message: '',
          messageType: 'audio',
          fileUrl: localBlobUrl,
          fileType: 'audio/webm',
          fileName: 'audio.webm',
          fileSize: audioBlob.size,
          caption: null,
          senderType: 'agent',
          senderName: user?.name || 'Você',
          senderProfilePic: user?.profilePic || null,
          createdAt: new Date().toISOString(),
          editedAt: null,
          deletedAt: null,
          seenByAgentAt: null,
          seenByUserAt: null,
          sending: true,
        }
        setMessages((prev) => [...prev, tempMessage])

        const formData = new FormData()
        // Usa o mimetype REAL do MediaRecorder (Safari grava audio/mp4, não
        // webm) — nome/extensão coerentes com o conteúdo. A flag isVoiceMessage
        // garante a conversão para OGG no backend independente da detecção.
        const realMime = audioBlob.type || 'audio/webm'
        const audioExt = realMime.includes('mp4')
          ? 'mp4'
          : realMime.includes('ogg')
            ? 'ogg'
            : 'webm'
        const audioFile = new File([audioBlob], `audio.${audioExt}`, { type: realMime })
        formData.append('file', audioFile)
        formData.append('isVoiceMessage', 'true')

        const uploadRes = await api.post('/upload/file', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        // Arquivo já está no servidor: um "Reenviar" depois daqui reaproveita a URL
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? {
                  ...m,
                  fileUrl: uploadRes.data.url,
                  fileType: uploadRes.data.fileType || 'audio/ogg',
                  fileName: uploadRes.data.fileName || 'audio.ogg',
                }
              : m
          )
        )

        const sent = await api.post(`/messages/${chatId}`, {
          message: '',
          messageType: 'audio',
          fileUrl: uploadRes.data.url,
          fileType: uploadRes.data.fileType || 'audio/ogg',
          fileName: uploadRes.data.fileName || 'audio.ogg',
          fileSize: uploadRes.data.fileSize || audioBlob.size,
          clientMessageId,
          ...(replyingTo ? { replyToMessageId: replyingTo.id } : {}),
        })
        confirmSent(tempId, sent.data)

        URL.revokeObjectURL(localBlobUrl)
        setAudioBlob(null)
        setRecordingDuration(0)
      }
      // Upload files first if any - usando hook com barra de progresso
      else if (uploadingFiles.length > 0) {
        for (const file of uploadingFiles) {
          const tempId = `temp-${Date.now()}-${file.name}`
          const clientMessageId = newClientMessageId()
          const messageType = file.type.startsWith('image/')
            ? 'image'
            : file.type.startsWith('video/')
            ? 'video'
            : file.type.startsWith('audio/')
            ? 'audio'
            : 'file'

          // Adicionar mensagem temporária
          const tempMessage: Message = {
            id: tempId,
            clientMessageId,
            message: newMessage.trim() || '',
            messageType,
            fileUrl: URL.createObjectURL(file),
            fileType: file.type,
            fileName: file.name,
            fileSize: file.size,
            caption: newMessage.trim() || null,
            senderType: 'agent',
            senderName: user?.name || 'Você',
            senderProfilePic: user?.profilePic || null,
            createdAt: new Date().toISOString(),
            editedAt: null,
            deletedAt: null,
            seenByAgentAt: null,
            seenByUserAt: null,
            sending: true,
          }
          setMessages((prev) => [...prev, tempMessage])

          try {
            // Usar hook de upload com progresso
            const fileUrl = await uploadFile(file)
            setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, fileUrl } : m)))

            const sent = await api.post(`/messages/${chatId}`, {
              message: '',
              messageType,
              fileUrl,
              fileType: file.type,
              fileName: file.name,
              fileSize: file.size,
              caption: newMessage.trim() || null,
              clientMessageId,
              ...(replyingTo ? { replyToMessageId: replyingTo.id } : {}),
            })
            confirmSent(tempId, sent.data)
          } catch (uploadError) {
            // Marcar mensagem específica como erro
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === tempId ? { ...msg, sending: false, error: true } : msg
              )
            )
            throw uploadError
          }
        }
        setUploadingFiles([])
      } else {
        // Send text message - adicionar mensagem temporária
        const tempId = `temp-${Date.now()}`
        const clientMessageId = newClientMessageId()
        const tempMessage: Message = {
          id: tempId,
          clientMessageId,
          message: newMessage.trim(),
          messageType: 'text',
          fileUrl: null,
          fileType: null,
          fileName: null,
          fileSize: null,
          caption: null,
          senderType: 'agent',
          senderName: user?.name || 'Você',
          senderProfilePic: user?.profilePic || null,
          createdAt: new Date().toISOString(),
          editedAt: null,
          deletedAt: null,
          seenByAgentAt: null,
          seenByUserAt: null,
          sending: true,
          replyToMessageId: replyingTo?.id ? Number(replyingTo.id) : null,
          replyToUserName: replyingTo?.senderName || null,
          replyToMessage: replyingTo?.message || null,
        }
        setMessages((prev) => [...prev, tempMessage])

        const sent = await api.post(`/messages/${chatId}`, {
          message: newMessage.trim(),
          messageType: 'text',
          clientMessageId,
          ...(replyingTo ? { replyToMessageId: replyingTo.id } : {}),
        })
        confirmSent(tempId, sent.data)
      }

      setNewMessage('')
      setReplyingTo(null)
      if (textareaRef.current) textareaRef.current.style.height = 'auto'
    } catch (error: any) {
      // Marcar mensagens temporárias como erro
      setMessages((prev) =>
        prev.map((msg) =>
          msg.sending ? { ...msg, sending: false, error: true } : msg
        )
      )
      
      // Tratamento robusto de erros para evitar crash
      const errorData = error.response?.data
      let errorMsg = 'Tente novamente'
      
      if (typeof errorData?.error === 'string') {
        errorMsg = errorData.error
      } else if (Array.isArray(errorData?.error)) {
        errorMsg = errorData.error.map((e: any) => e.message || String(e)).join(', ')
      } else if (errorData?.error?.message) {
        errorMsg = errorData.error.message
      } else if (errorData?.message) {
        errorMsg = errorData.message
      }
      
      toast({
        title: 'Erro ao enviar mensagem',
        description: errorMsg,
        status: 'error',
        duration: 5000,
      })
    } finally {
      sendingRef.current = false
      setIsSending(false)
    }
  }

  // Handle finish chat
  const handleFinish = async () => {
    try {
      await api.post(`/chats/${chatId}/finish`)
      toast({
        title: 'Atendimento concluído',
        status: 'success',
        duration: 3000,
      })
      onChatFinished?.()
    } catch (error: any) {
      toast({
        title: 'Erro ao concluir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  // Handle status change
  const handleStatusChange = async () => {
    if (!selectedStatus) return

    const status = customStatuses.find((s) => s.id === selectedStatus)
    if (status?.requiresReason && !statusReason.trim()) {
      toast({
        title: 'Motivo obrigatório',
        description: 'Este status requer um motivo',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    try {
      await api.put(`/chats/${chatId}/status`, {
        customStatusId: selectedStatus,
        reason: statusReason.trim() || null,
      })
      toast({
        title: 'Status atualizado',
        status: 'success',
        duration: 3000,
      })
      onStatusModalClose()
      setSelectedStatus(null)
      setStatusReason('')
      // Refresh chat
      const res = await api.get(`/chats/${chatId}`)
      setChat(res.data)
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar status',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  // Handle transfer
  const handleTransfer = async () => {
    if (!transferAgentId) return

    try {
      await api.post(`/chats/${chatId}/transfer`, {
        toAgentId: transferAgentId,
        reason: transferReason.trim() || null,
      })
      toast({
        title: 'Atendimento transferido',
        status: 'success',
        duration: 3000,
      })
      onTransferModalClose()
      setTransferAgentId(null)
      setTransferReason('')
    } catch (error: any) {
      toast({
        title: 'Erro ao transferir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  // Fetch available agents for transfer
  const openTransferModal = async () => {
    try {
      const res = await api.get('/agents')
      const agents = res.data.data || res.data
      setAvailableAgents(Array.isArray(agents) ? agents : [])
      onTransferModalOpen()
    } catch (error) {
      console.error('Error fetching agents:', error)
    }
  }

  // Open category change modal
  const openCategoryModal = async () => {
    try {
      const res = await api.get('/categories')
      setCategories(res.data)
      setSelectedCategoryId('')
      onCategoryModalOpen()
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  // Handle category change
  const handleCategoryChange = async () => {
    if (!selectedCategoryId) return

    try {
      await api.put(`/chats/${chatId}/category`, {
        categoryId: Number(selectedCategoryId),
      })
      toast({
        title: 'Categoria atualizada',
        status: 'success',
        duration: 3000,
      })
      onCategoryModalClose()
      const res = await api.get(`/chats/${chatId}`)
      setChat(res.data)
    } catch (error: any) {
      toast({
        title: 'Erro ao alterar categoria',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  // Agente de IA ativo da categoria deste chat (admin) — para "Transferir para IA"
  const aiAgentForCategory = aiAgents.find(
    (a) =>
      Number(a.categoryId) === Number(chat?.categoryId) &&
      a.isActive &&
      a.hasToken &&
      a.botUserId
  )
  const canAssignToAi =
    canManageSystem &&
    !!chat &&
    chat.status !== 'completed' &&
    !!aiAgentForCategory &&
    Number(chat.assignedAgentId) !== Number(aiAgentForCategory.botUserId)

  const handleAssignToAi = async () => {
    if (!aiAgentForCategory) return
    const confirmed = window.confirm(
      `Transferir este atendimento para o agente de IA ${aiAgentForCategory.name}? A IA passa a responder o cliente imediatamente.`
    )
    if (!confirmed) return
    try {
      await api.post(`/admin/ai-agents/chats/${chatId}/assign-ai`)
      toast({
        title: `Chamado transferido para ${aiAgentForCategory.name}`,
        status: 'success',
        duration: 4000,
      })
      const res = await api.get(`/chats/${chatId}`)
      setChat(res.data)
    } catch (error: any) {
      toast({
        title: 'Erro ao transferir para a IA',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  if (isLoading) {
    return (
      <Flex h="100%" align="center" justify="center">
        <Spinner size="xl" color="yellow.500" />
      </Flex>
    )
  }

  return (
    <Flex h="100%" direction="column" {...getRootProps()}>
      <input {...getInputProps()} />

      {/* Drag overlay */}
      {isDragActive && (
        <Flex
          position="absolute"
          inset={0}
          bg="blackAlpha.800"
          zIndex={10}
          align="center"
          justify="center"
          borderRadius="lg"
        >
          <VStack spacing={4}>
            <Paperclip size={64} color="#ECC94B" />
            <Text fontSize="xl" color="white" fontWeight="bold">
              Solte os arquivos aqui
            </Text>
          </VStack>
        </Flex>
      )}

      {/* Header */}
      <HStack
        px={{ base: 2, md: 4 }}
        py={{ base: 2, md: 3 }}
        borderBottom="1px"
        borderColor={borderColor}
        bg={surfaceBg}
        justify="space-between"
        flexShrink={0}
        spacing={2}
      >
        <HStack spacing={{ base: 2, md: 3 }} flex={1} minW={0}>
          {onClose && (
            <IconButton
              aria-label="Voltar para as conversas"
              icon={<CaretLeft size={24} />}
              size="md"
              variant="ghost"
              ml={-1}
              onClick={onClose}
              color={mutedColor}
              _hover={{ color: headerTextColor, bg: hoverBg }}
              display={{ base: 'flex', lg: 'none' }}
              flexShrink={0}
            />
          )}
          <Avatar
            size={{ base: 'sm', md: 'sm' }}
            name={chat?.ownerName}
            src={chat?.ownerProfilePic || undefined}
            bg="yellow.500"
            color="gray.900"
            flexShrink={0}
          />
          <VStack align="start" spacing={0} flex={1} minW={0}>
            <Text fontWeight="semibold" color={headerTextColor} fontSize={{ base: 'sm', md: 'md' }} noOfLines={1}>
              {chat?.ownerName}
            </Text>
            {/* Visitante ainda não vinculado: o dono é um usuário-sombra; o
                e-mail que vale é o informado na tela de login. */}
            {isWhatsappChat && (
              <Text fontSize={{ base: '2xs', md: 'xs' }} color={mutedColor} noOfLines={1}>
                {chat?.whatsappWaId ? formatWaId(chat.whatsappWaId) : ''}
                {chat?.whatsappProfileName ? ` · ${chat.whatsappProfileName}` : ''}
              </Text>
            )}
            {chat?.isGuest && !chat?.guestLinkedUserId && !isWhatsappChat && (
              <Text fontSize={{ base: '2xs', md: 'xs' }} color={mutedColor} noOfLines={1}>
                {chat?.guestEmail && !isPlaceholderGuestEmail(chat.guestEmail) ? chat.guestEmail : 'e-mail não informado'} · veio da tela de login (sem conta vinculada)
              </Text>
            )}
            {isTyping ? (
              <Text fontSize={{ base: '2xs', md: 'xs' }} color="green.400" fontStyle="italic" noOfLines={1}>
                digitando...
              </Text>
            ) : (
              <HStack spacing={1} flexWrap="wrap" mt={0.5}>
                <Badge bg={chat?.categoryColor || 'blue.500'} color="white" fontSize={{ base: '2xs', md: 'xs' }} noOfLines={1}>
                  {chat?.categoryName || 'Sem categoria'}
                </Badge>
                {isWhatsappChat && <WhatsappBadge />}
                {chat?.isGuest && !isWhatsappChat && <GuestBadge />}
                {chat?.customStatusName && (
                  <Badge bg={chat?.customStatusColor} color="white" fontSize={{ base: '2xs', md: 'xs' }} noOfLines={1}>
                    {chat?.customStatusName}
                  </Badge>
                )}
              </HStack>
            )}
            {/* Planos e acessos do vendedor — sempre visíveis, inclusive
                enquanto ele digita. */}
            {chat?.ownerTags?.length > 0 && (
              <Box mt={1} maxW="100%">
                <AccessTags tags={chat.ownerTags} size="xs" max={4} />
              </Box>
            )}
          </VStack>
        </HStack>
        <HStack spacing={1} flexShrink={0}>
          <IconButton
            aria-label="Info do usuário"
            icon={<UserCircle size={20} />}
            variant="ghost"
            onClick={onToggleUserInfo}
            size="md"
          />
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Opções"
              icon={<DotsThreeVertical size={22} />}
              variant="ghost"
              size="md"
            />
            <MenuList>
              <MenuItem icon={<Flag size={18} />} onClick={onStatusModalOpen}>
                Alterar Status
              </MenuItem>
              <MenuItem icon={<Tag size={18} />} onClick={openCategoryModal}>
                Alterar Categoria
              </MenuItem>
              {chat?.status !== 'completed' && (
                <MenuItem icon={<ArrowsLeftRight size={18} />} onClick={openTransferModal}>
                  Transferir
                </MenuItem>
              )}
              {canAssignToAi && (
                <MenuItem icon={<Robot size={18} />} onClick={handleAssignToAi} color="purple.400">
                  Transferir para IA ({aiAgentForCategory.name})
                </MenuItem>
              )}
              {chat?.status !== 'completed' && (
                <MenuItem icon={<CheckCircle size={18} />} onClick={handleFinish} color="green.400">
                  Concluir Atendimento
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </HStack>
      </HStack>

      {/* Transfer reason banner - only visible to agents */}
      {showTransferBanner && chat?.lastTransfer && (
        <Alert status="info" variant="subtle" bg={transferBannerBg} borderRadius={0}>
          <Info size={20} weight="fill" color="#63B3ED" />
          <Box flex={1} ml={3}>
            <AlertDescription color={transferBannerText} fontSize="sm">
              <Text as="span" fontWeight="semibold" color={transferBannerLabel}>
                Transferido por {chat.lastTransfer.fromAgentName}
              </Text>
              {chat.lastTransfer.reason ? `: ${chat.lastTransfer.reason}` : ' (sem motivo informado)'}
            </AlertDescription>
          </Box>
          <CloseButton
            color={transferBannerClose}
            size="sm"
            onClick={() => setShowTransferBanner(false)}
          />
        </Alert>
      )}

      {/* Messages */}
      <Box flex={1} overflow="hidden" minH={0}>
        <VStack
          ref={messagesContainerRef}
          h="100%"
          overflowY="auto"
          overflowX="hidden"
          px={{ base: 2, md: 4 }}
          py={{ base: 2, md: 4 }}
          spacing={0}
          align="stretch"
          sx={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
        >
          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              isOwn={msg.senderType === 'agent'}
              onImageClick={setLightboxImage}
              onRetry={handleRetry}
              onEdit={handleEditMessage}
              onDelete={handleDeleteMessage}
              onReply={(m) => setReplyingTo(m)}
              waStatus={chat?.channel === 'whatsapp' && msg.senderType === 'agent' ? waStatuses[String(msg.id)] : undefined}
            />
          ))}
          {isTyping && (
            <Flex justify="flex-start" mb={2}>
              <Badge colorScheme="gray" px={3} py={1}>
                {typingUser || 'Usuário'} está digitando...
              </Badge>
            </Flex>
          )}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      {/* Upload progress */}
      {uploads.size > 0 && (
        <Box px={4} py={2} borderTop="1px" borderColor={borderColor} bg={surfaceBg}>
          <UploadProgressList uploads={uploads} onCancel={cancelUpload} onDismiss={clearUpload} />
        </Box>
      )}

      {/* File preview */}
      {uploadingFiles.length > 0 && (
        <HStack
          px={4}
          py={2}
          borderTop="1px"
          borderColor={borderColor}
          bg={surfaceBg}
          flexWrap="wrap"
          gap={2}
        >
          {uploadingFiles.map((file, index) => (
            <HStack
              key={index}
              bg={subtleBg}
              px={3}
              py={2}
              borderRadius="lg"
              spacing={2}
            >
              {file.type.startsWith('image/') ? (
                <ImageIcon size={16} />
              ) : file.type.startsWith('video/') ? (
                <Play size={16} />
              ) : (
                <FileDoc size={16} />
              )}
              <Text fontSize="sm" noOfLines={1} maxW="100px">
                {file.name}
              </Text>
              <IconButton
                aria-label="Remover"
                icon={<X size={14} />}
                size="xs"
                variant="ghost"
                onClick={() =>
                  setUploadingFiles((prev) => prev.filter((_, i) => i !== index))
                }
              />
            </HStack>
          ))}
        </HStack>
      )}

      {/* Audio recording preview */}
      {(isRecording || audioBlob) && (
        <HStack
          px={4}
          py={3}
          borderTop="1px solid"
          borderColor={borderColor}
          bg={surfaceBg}
          spacing={4}
        >
          {isRecording ? (
            <>
              <Box flex={1}>
                <HStack spacing={3}>
                  <Box w={3} h={3} borderRadius="full" bg="red.500" animation="pulse 1s infinite" />
                  <Text color={recordingTextColor} fontSize="sm">Gravando...</Text>
                  <Text color={mutedColor} fontSize="sm">{formatDuration(recordingDuration)}</Text>
                </HStack>
                <Progress
                  size="xs"
                  value={(recordingDuration % 60) / 60 * 100}
                  colorScheme="red"
                  mt={2}
                />
              </Box>
              <IconButton
                aria-label="Cancelar"
                icon={<X size={20} />}
                colorScheme="red"
                variant="ghost"
                onClick={cancelRecording}
              />
              <IconButton
                aria-label="Parar"
                icon={<Stop size={20} weight="fill" />}
                colorScheme="yellow"
                onClick={stopRecording}
              />
            </>
          ) : audioBlob && (
            <>
              <audio src={URL.createObjectURL(audioBlob)} controls style={{ flex: 1 }} />
              <IconButton
                aria-label="Cancelar"
                icon={<X size={20} />}
                colorScheme="red"
                variant="ghost"
                onClick={() => {
                  setAudioBlob(null)
                  setRecordingDuration(0)
                }}
              />
              <IconButton
                aria-label="Enviar"
                icon={<PaperPlaneRight size={20} weight="fill" />}
                colorScheme="yellow"
                onClick={handleSend}
                isLoading={isSending}
              />
            </>
          )}
        </HStack>
      )}

      {/* Input */}
      {!isRecording && !audioBlob && (
        <Box position="relative" borderTop="1px" borderColor={borderColor} bg={surfaceBg} flexShrink={0} pb={{ base: 'env(safe-area-inset-bottom)', lg: 0 }}>
          {showEmojiPicker && (
            <Box
              ref={emojiPickerRef}
              position="absolute"
              bottom="100%"
              right={{ base: 2, md: 4 }}
              left={{ base: 2, md: 'auto' }}
              zIndex={20}
              mb={1}
              maxW={{ base: 'calc(100% - 16px)', md: 'auto' }}
            >
              <Picker
                data={data}
                onEmojiSelect={handleEmojiSelect}
                theme={colorMode}
                locale="pt"
                previewPosition="none"
                skinTonePosition="search"
                maxFrequentRows={2}
              />
            </Box>
          )}
          {replyingTo && (
            <HStack
              px={{ base: 2, md: 4 }}
              py={2}
              borderBottom="1px"
              borderColor={replyBorderColor}
              spacing={2}
            >
              <Box
                flex={1}
                borderLeft="3px solid"
                borderColor="yellow.400"
                pl={2}
                minW={0}
              >
                <Text fontSize={{ base: '2xs', md: 'xs' }} fontWeight="bold" color={replyNameColor} noOfLines={1}>
                  {replyingTo.senderName}
                </Text>
                <Text fontSize={{ base: '2xs', md: 'xs' }} color={replyMsgColor} noOfLines={1}>
                  {replyingTo.message || (replyingTo.messageType === 'image' ? '📷 Imagem' : replyingTo.messageType === 'audio' ? '🎤 Áudio' : replyingTo.messageType === 'video' ? '🎥 Vídeo' : '📎 Arquivo')}
                </Text>
              </Box>
              <IconButton
                aria-label="Cancelar resposta"
                icon={<X size={16} />}
                size="xs"
                variant="ghost"
                onClick={() => setReplyingTo(null)}
                flexShrink={0}
              />
            </HStack>
          )}
          <HStack px={{ base: 2, md: 4 }} py={{ base: 2, md: 3 }} spacing={{ base: 1, md: 2 }} align="flex-end">
            <HStack spacing={{ base: 0.5, md: 1 }} flexShrink={0}>
              <IconButton
                aria-label="Anexar arquivo"
                icon={<Paperclip size={18} />}
                variant="ghost"
                onClick={() => {
                  const input = document.createElement('input')
                  input.type = 'file'
                  input.multiple = true
                  input.accept = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar'
                  input.onchange = (e: any) => {
                    const files = Array.from(e.target.files) as File[]
                    setUploadingFiles(files)
                  }
                  input.click()
                }}
                isDisabled={chat?.status === 'completed'}
                size={{ base: 'sm', md: 'sm' }}
                flexShrink={0}
              />
              <IconButton
                aria-label="Gravar áudio"
                icon={<Microphone size={18} />}
                variant="ghost"
                onClick={startRecording}
                isDisabled={chat?.status === 'completed'}
                size={{ base: 'sm', md: 'sm' }}
                flexShrink={0}
              />
              <IconButton
                aria-label="Emojis"
                icon={<Smiley size={18} />}
                variant="ghost"
                onClick={() => setShowEmojiPicker(prev => !prev)}
                isDisabled={chat?.status === 'completed'}
                size={{ base: 'sm', md: 'sm' }}
                flexShrink={0}
              />
            </HStack>
            <Box flex={1} position="relative" minW={0}>
              {showQuickReplyMenu && filteredQuickReplies.length > 0 && (
                <Box
                  position="absolute"
                  bottom="100%"
                  left={0}
                  right={0}
                  mb={1}
                  bg={quickReplyBg}
                  borderRadius="md"
                  border="1px solid"
                  borderColor={quickReplyBorder}
                  maxH="200px"
                  overflowY="auto"
                  zIndex={10}
                  boxShadow="lg"
                >
                  {filteredQuickReplies.map((reply, idx) => (
                    <HStack
                      key={reply.id}
                      px={3}
                      py={2}
                      cursor="pointer"
                      bg={idx === quickReplyIndex ? quickReplyActiveBg : 'transparent'}
                      _hover={{ bg: quickReplyActiveBg }}
                      onClick={() => handleQuickReplySelect(reply)}
                      spacing={3}
                    >
                      <Badge colorScheme="yellow" fontSize="xs" flexShrink={0}>/{reply.command}</Badge>
                      <Text fontSize="sm" color={quickReplyTextColor} noOfLines={1}>{reply.description}</Text>
                    </HStack>
                  ))}
                </Box>
              )}
              {isWhatsappChat && (
                <Text fontSize="2xs" color={mutedColor} mb={1}>
                  Anexos: imagem até 5 MB, áudio/vídeo 16 MB, documento 100 MB
                </Text>
              )}
              <Textarea
                ref={textareaRef}
                placeholder={chat?.status === 'completed' ? 'Atendimento concluído' : 'Digite uma mensagem...'}
                value={newMessage}
                onChange={(e) => {
                  const val = e.target.value
                  setNewMessage(val)
                  if (val.startsWith('/')) {
                    setShowQuickReplyMenu(true)
                    setQuickReplyFilter(val.slice(1))
                    setQuickReplyIndex(0)
                  } else {
                    setShowQuickReplyMenu(false)
                  }
                  handleTyping()
                }}
                onKeyDown={(e) => {
                  if (showQuickReplyMenu && filteredQuickReplies.length > 0) {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      setQuickReplyIndex((prev) => Math.min(prev + 1, filteredQuickReplies.length - 1))
                      return
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault()
                      setQuickReplyIndex((prev) => Math.max(prev - 1, 0))
                      return
                    }
                    if (e.key === 'Enter' || e.key === 'Tab') {
                      e.preventDefault()
                      handleQuickReplySelect(filteredQuickReplies[quickReplyIndex])
                      return
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault()
                      setShowQuickReplyMenu(false)
                      return
                    }
                  }
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                onPaste={handlePaste}
                variant="outline"
                disabled={chat?.status === 'completed'}
                rows={1}
                minH={{ base: '36px', md: '40px' }}
                maxH="150px"
                resize="none"
                overflow="auto"
                fontSize={{ base: 'sm', md: 'md' }}
                w="100%"
                minW={0}
              />
            </Box>
            <IconButton
              aria-label="Enviar"
              icon={<PaperPlaneRight size={18} weight="fill" />}
              colorScheme="yellow"
              onClick={handleSend}
              isLoading={isSending}
              isDisabled={chat?.status === 'completed' || (!newMessage.trim() && uploadingFiles.length === 0)}
              size={{ base: 'sm', md: 'sm' }}
              flexShrink={0}
            />
          </HStack>
        </Box>
      )}

      {/* Lightbox */}
      <Lightbox
        open={!!lightboxImage}
        close={() => setLightboxImage(null)}
        slides={lightboxImage ? [{ src: lightboxImage }] : []}
      />

      {/* Status Modal */}
      <Modal isOpen={isStatusModalOpen} onClose={onStatusModalClose} size={{ base: 'full', sm: 'md' }} isCentered>
        <ModalOverlay />
        <ModalContent mx={{ base: 0, sm: 'auto' }}>
          <ModalHeader fontSize={{ base: 'sm', md: 'md' }}>Alterar Status</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Select
                placeholder="Selecione um status"
                value={selectedStatus || ''}
                onChange={(e) => setSelectedStatus(Number(e.target.value) || null)}
              >
                <option value="">Remover status customizado</option>
                {customStatuses.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </Select>
              {selectedStatus &&
                customStatuses.find((s) => s.id === selectedStatus)?.requiresReason && (
                  <Textarea
                    placeholder="Motivo (obrigatório)"
                    value={statusReason}
                    onChange={(e) => setStatusReason(e.target.value)}
                  />
                )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onStatusModalClose}>
              Cancelar
            </Button>
            <Button colorScheme="yellow" onClick={handleStatusChange}>
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Transfer Modal */}
      <Modal isOpen={isTransferModalOpen} onClose={onTransferModalClose} size={{ base: 'full', sm: 'md' }} isCentered>
        <ModalOverlay />
        <ModalContent mx={{ base: 0, sm: 'auto' }}>
          <ModalHeader fontSize={{ base: 'sm', md: 'md' }}>Transferir Atendimento</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Select
                placeholder="Selecione um atendente"
                value={transferAgentId || ''}
                onChange={(e) => setTransferAgentId(Number(e.target.value) || null)}
              >
                {availableAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} ({agent.email})
                  </option>
                ))}
              </Select>
              <Textarea
                placeholder="Motivo da transferência (opcional)"
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTransferModalClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="yellow"
              onClick={handleTransfer}
              isDisabled={!transferAgentId}
            >
              Transferir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Category Change Modal */}
      <Modal isOpen={isCategoryModalOpen} onClose={onCategoryModalClose} isCentered size={{ base: 'full', sm: 'md' }}>
        <ModalOverlay />
        <ModalContent borderColor={borderColor} mx={{ base: 0, sm: 'auto' }}>
          <ModalHeader fontSize={{ base: 'sm', md: 'md' }}>Alterar Categoria</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text color={categoryModalTextColor} mb={3}>
              Categoria atual: <Badge bg={chat?.categoryColor || 'blue.500'} color="white" fontSize="xs">{chat?.categoryName || 'Sem categoria'}</Badge>
            </Text>
            <Select
              placeholder="Selecione a nova categoria"
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              variant="outline"
              bg={categoryModalSelectBg}
              borderColor={categoryModalSelectBorder}
              _hover={{ borderColor: mutedColor }}
              color={categoryModalSelectColor}
            >
              {categories
                .filter((cat) => cat.id !== chat?.categoryId)
                .map((cat) => (
                  <option key={cat.id} value={cat.id} style={{ background: categoryOptionBg }}>
                    {cat.name}
                  </option>
                ))}
            </Select>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onCategoryModalClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="yellow"
              onClick={handleCategoryChange}
              isDisabled={!selectedCategoryId}
            >
              Alterar Categoria
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  )
}
